/**
 * Quick actions for the dashboard.
 *
 * The dashboard had no action of any kind — the only controls on it were
 * Refresh, a time-span stepper and Customize Layout. Everything a person came
 * to do lived at least two clicks away through the sidebar, and the three jobs
 * this screen serves (diet tech, kitchen, dietitian) do not want the same
 * three things.
 *
 * An action is an id, a label and a way to gate it. Definitions live here
 * rather than in the saved row, so what a user stores is `['visualizer', ...]`
 * — renaming a screen or moving a route does not require rewriting every
 * user's preference.
 */

export type DashboardActionCommand = 'visualizer' | 'printTrayTickets';

export interface DashboardActionDef {
  id: string;
  /** Static label, or a key telling the resolver to substitute a facility term. */
  label: string;
  icon: string;
  /** Where it goes. Exactly one of `route` or `command`. */
  route?: string;
  /** Something that happens in place rather than a navigation. */
  command?: DashboardActionCommand;
  /** Security field that must be >= 1. Same fields the sidebar gates on. */
  securityField?: string;
  /** Only offered when the facility has KDS switched on. */
  requiresKds?: boolean;
  /** Only offered when the facility uses assessments (UseAssessment). */
  requiresAssessments?: boolean;
  /** Substitute the facility's word for residents into `label`. */
  usesResidentLabel?: boolean;
}

/**
 * Every action a user may pin.
 *
 * Deliberately short. A picker of forty things is a sidebar, and the sidebar
 * already exists — this is the handful worth reaching for without navigating.
 */
export const DASHBOARD_ACTIONS: DashboardActionDef[] = [
  {
    id: 'visualizer',
    label: 'Tray Ticket Visualizer',
    icon: 'bi-easel',
    command: 'visualizer',
    securityField: 'dietech_Resident_Level',
  },
  {
    id: 'selective-touch',
    label: 'Selective Touch',
    icon: 'bi-hand-index-thumb',
    route: '/selective-touch',
    securityField: 'dietech_Selective_Menu_Touch_Level',
  },
  {
    id: 'weight-entry',
    label: 'Weight Entry',
    icon: 'bi-speedometer',
    route: '/weight-entry',
    securityField: 'dietech_Nutritional_Assessment_Level',
  },
  {
    id: 'print-tray-tickets',
    label: 'Print Tray Tickets',
    icon: 'bi-printer',
    command: 'printTrayTickets',
    securityField: 'dietech_Report_Level',
  },
  {
    id: 'residents',
    label: 'Residents',
    icon: 'bi-people',
    route: '/people',
    securityField: 'dietech_Resident_Level',
    usesResidentLabel: true,
  },
  {
    id: 'menu-cycle',
    label: 'Menu Cycle',
    icon: 'bi-calendar3',
    route: '/menucycle-hub',
    securityField: 'dietech_Menu_Cycle_Level',
  },
  {
    id: 'assessments',
    label: 'Assessments',
    icon: 'bi-clipboard2-check',
    route: '/assessments',
    securityField: 'dietech_Nutritional_Assessment_Level',
    requiresAssessments: true,
  },
  {
    id: 'clinical-nutrition',
    label: 'Clinical Nutrition',
    icon: 'bi-heart-pulse',
    route: '/clinical-nutrition',
    securityField: 'dietech_Nutritional_Assessment_Level',
  },
  {
    id: 'kitchen',
    label: 'Kitchen Display',
    icon: 'bi-tv',
    route: '/kitchen',
    securityField: 'dietech_Kitchen_Display_Level',
    requiresKds: true,
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'bi-file-earmark-bar-graph',
    route: '/reports',
    securityField: 'dietech_Report_Level',
  },
  {
    id: 'emr',
    label: 'EMR / EHR',
    icon: 'bi-hospital',
    route: '/emr',
    securityField: 'dietech_Ehr_Level',
  },
  {
    id: 'purchasing',
    label: 'Purchasing',
    icon: 'bi-bag',
    route: '/ordering',
    securityField: 'pur_Level',
  },
];

/**
 * What a user gets before they have chosen anything.
 *
 * These four are the shift's opening moves: look at a ticket, enter the
 * selections, record the weights, print the line. The Visualizer leads because
 * the WinForms ribbon gave it a full-size button and it is the single
 * most-pressed control on the resident screen.
 *
 * Anything a user has no access to is filtered out at render, so a set of four
 * can safely include things not everyone can open.
 */
export const DEFAULT_ACTION_IDS = [
  'visualizer',
  'selective-touch',
  'weight-entry',
  'print-tray-tickets',
];

/**
 * Below four the row looks unfinished; above eight it has stopped being quick
 * and become a second sidebar.
 */
export const MAX_ACTIONS = 8;

export function findAction(id: string): DashboardActionDef | undefined {
  return DASHBOARD_ACTIONS.find((a) => a.id === id);
}
